import { useRef, type ReactNode } from 'react'

import { clsx } from 'clsx'

import { DefaultOption } from './DefaultOption'

import type { SelectOption } from './types'

interface OptionsListProps<T> {
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  highlightedIndex: number
  isLoading: boolean
  noOptionsText: string
  virtualized: boolean
  itemHeight: number
  maxVisibleItems: number
  listHeight: number
  totalHeight: number
  startIndex: number
  endIndex: number
  offsetY: number
  isMobile: boolean
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void
}

function LoadingPlaceholder(): JSX.Element {
  return (
    <div className="px-3 py-4 text-center text-muted-foreground">
      <svg className="animate-spin h-5 w-5 mx-auto mb-2" fill="none" viewBox="0 0 24 24">
        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
        <path
          className="opacity-75"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          fill="currentColor"
        />
      </svg>
      Loading...
    </div>
  )
}

function computeListStyle(virtualized: boolean, listHeight: number, optionsLen: number, maxVisibleItems: number): React.CSSProperties | undefined {
  if (virtualized) { return { height: listHeight, position: 'relative' } }
  if (optionsLen > maxVisibleItems) { return { maxHeight: listHeight } }
  return undefined
}

export function OptionsList<T>(p: OptionsListProps<T>): JSX.Element {
  const listRef = useRef<HTMLDivElement>(null)

  if (p.isLoading) { return <LoadingPlaceholder /> }
  if (p.filteredOptions.length === 0) {
    return (
      <div className="px-3 py-4 text-center text-muted-foreground">
        {p.noOptionsText}
      </div>
    )
  }

  const visibleOptions = p.filteredOptions.slice(p.startIndex, p.endIndex)
  const listStyle = computeListStyle(p.virtualized, p.listHeight, p.filteredOptions.length, p.maxVisibleItems)

  return (
    <div
      ref={listRef}
      className={clsx(
        p.isMobile && 'max-h-[40vh]',
        (p.isMobile || p.filteredOptions.length > p.maxVisibleItems) && 'overflow-y-auto',
      )}
      style={listStyle}
      onScroll={p.virtualized ? p.onScroll : undefined}
    >
      {p.virtualized && (
        <VirtualizedOptions
          endIndex={p.endIndex}
          highlightedIndex={p.highlightedIndex}
          itemHeight={p.itemHeight}
          offsetY={p.offsetY}
          renderOption={p.renderOption}
          startIndex={p.startIndex}
          totalHeight={p.totalHeight}
          value={p.value}
          visibleOptions={visibleOptions}
          onSelect={p.onSelect}
          onSetHighlightedIndex={p.onSetHighlightedIndex}
        />
      )}
      {!p.virtualized && (
        <NonVirtualizedOptions
          filteredOptions={p.filteredOptions}
          highlightedIndex={p.highlightedIndex}
          renderOption={p.renderOption}
          value={p.value}
          onSelect={p.onSelect}
          onSetHighlightedIndex={p.onSetHighlightedIndex}
        />
      )}
    </div>
  )
}

interface VirtualizedOptionsProps<T> {
  visibleOptions: SelectOption<T>[]
  startIndex: number
  endIndex: number
  value: T | null | undefined
  highlightedIndex: number
  itemHeight: number
  totalHeight: number
  offsetY: number
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
}

function VirtualizedOptions<T>({
  visibleOptions,
  startIndex,
  value,
  highlightedIndex,
  itemHeight,
  totalHeight,
  offsetY,
  renderOption,
  onSelect,
  onSetHighlightedIndex,
}: VirtualizedOptionsProps<T>): JSX.Element {
  return (
    <div style={{ height: totalHeight, position: 'relative' }}>
      <div style={{ transform: `translateY(${offsetY}px)` }}>
        {visibleOptions.map((option, idx) => {
          const actualIndex = startIndex + idx
          const isSelected = option.value === value
          const isHighlighted = actualIndex === highlightedIndex

          return (
            <OptionItem
              key={String(option.value)}
              highlightedIndex={actualIndex}
              isHighlighted={isHighlighted}
              isSelected={isSelected}
              itemHeight={itemHeight}
              option={option}
              renderOption={renderOption}
              onSelect={onSelect}
              onSetHighlightedIndex={onSetHighlightedIndex}
            />
          )
        })}
      </div>
    </div>
  )
}

interface NonVirtualizedOptionsProps<T> {
  filteredOptions: SelectOption<T>[]
  value: T | null | undefined
  highlightedIndex: number
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
}

function NonVirtualizedOptions<T>({
  filteredOptions,
  value,
  highlightedIndex,
  renderOption,
  onSelect,
  onSetHighlightedIndex,
}: NonVirtualizedOptionsProps<T>): JSX.Element {
  return (
    <>
      {filteredOptions.map((option, idx) => {
        const isSelected = option.value === value
        const isHighlighted = idx === highlightedIndex

        return (
          <OptionItem
            key={String(option.value)}
            highlightedIndex={idx}
            isHighlighted={isHighlighted}
            isSelected={isSelected}
            option={option}
            renderOption={renderOption}
            onSelect={onSelect}
            onSetHighlightedIndex={onSetHighlightedIndex}
          />
        )
      })}
    </>
  )
}

interface OptionItemProps<T> {
  option: SelectOption<T>
  isSelected: boolean
  isHighlighted: boolean
  highlightedIndex: number
  itemHeight?: number
  renderOption?: (option: SelectOption<T>, isSelected: boolean, isHighlighted: boolean) => ReactNode
  onSelect: (option: SelectOption<T>) => void
  onSetHighlightedIndex: (index: number) => void
}

function OptionItem<T>({
  option,
  isSelected,
  isHighlighted,
  highlightedIndex,
  itemHeight,
  renderOption,
  onSelect,
  onSetHighlightedIndex,
}: OptionItemProps<T>): JSX.Element {
  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onSelect(option)
    }
  }

  return (
    <div
      aria-selected={isSelected}
      role="option"
      style={itemHeight ? { height: itemHeight } : undefined}
      tabIndex={0}
      onClick={() => onSelect(option)}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => onSetHighlightedIndex(highlightedIndex)}
    >
      {renderOption
        ? renderOption(option, isSelected, isHighlighted)
        : <DefaultOption isHighlighted={isHighlighted} isSelected={isSelected} option={option} />}
    </div>
  )
}
